import { useEffect, useState } from 'react';
import { getPaperworkPrices } from 'lib/siteCommerceApi';
import { arrangementPackageCount } from 'components/paperwork/content';

// Package prices on the shop pages come only from the owner's saved prices (website launch L2.1; Ivan 2026-10-15:
// "those prices should be read from the database"). Until they arrive, or when none are saved, no price is shown
// (never a built-in number); checkout always confirms the price on the server.
export const PACKAGE_IDS = Object.freeze(['basic', 'standard', 'premium']);
const NONE = Object.freeze({});
let current = NONE, details = NONE, deposit = null, customization = null, loading = null;
const listeners = new Set();
let loadStatus = 'idle', loadedAt = 0, refreshUsers = 0, retryTimer = null, retryCount = 0;
const PRICE_CHANGE_KEY = 'magellan-paperwork-prices-updated';
const PRICE_CHANGE_EVENT = 'magellan:paperwork-prices-updated';
const CACHE_MS = 30000;
const clearRetry = () => { clearTimeout(retryTimer); retryTimer = null; };
const notify = () => listeners.forEach(listener => listener());
const valid = p => Number.isSafeInteger(p?.priceMinor) && p.priceMinor > 0
  && (p.onSale !== true || (Number.isSafeInteger(p.originalMinor) && p.originalMinor > p.priceMinor));
export const packagePrice = (id, arrangement) => current[id] === undefined ? undefined : current[id] * arrangementPackageCount(arrangement);
// Standard/Premium and Basic with both arrangements start with one review deposit.
export const isDeposit = (id, arrangement) => id === 'standard' || id === 'premium' || (id === 'basic' && arrangement === 'both');
export const chargePrice = (id, arrangement) => current[id] === undefined ? undefined : isDeposit(id, arrangement) ? deposit ?? undefined : packagePrice(id, arrangement);
// Basic customization is charged for each selected package when ticked.
export const customizationPrice = arrangement => (current.basic === undefined || customization === null ? undefined : customization * arrangementPackageCount(arrangement));
export function loadPackagePrices({ refresh = false } = {}) {
  if (loading) return loading;
  if (!refresh && loadStatus === 'ready' && Date.now() - loadedAt < CACHE_MS) return Promise.resolve(current);
  clearRetry();
  loadStatus = 'loading';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  // Assign the shared request before notifying subscribers, so every consumer joins it.
  loading = Promise.resolve().then(() => getPaperworkPrices(controller.signal)).then(data => {
    const entries = PACKAGE_IDS.map(id => [id, data?.packages?.[id]]);
    if (!entries.every(([, p]) => valid(p))) throw new Error('Saved package prices unavailable');
    details = Object.freeze(Object.fromEntries(entries.map(([id, p]) =>
      [id, Object.freeze({ priceMinor: p.priceMinor, originalMinor: p.onSale === true ? p.originalMinor : null, onSale: p.onSale === true })])));
    current = Object.freeze(Object.fromEntries(entries.map(([id, p]) => [id, p.priceMinor])));
    deposit = Number.isSafeInteger(data?.deposit?.priceMinor) && data.deposit.priceMinor > 0 ? data.deposit.priceMinor : null;
    customization = Number.isSafeInteger(data?.customization?.basic?.priceMinor) && data.customization.basic.priceMinor > 0 ? data.customization.basic.priceMinor : null;
    loadedAt = Date.now();
    loadStatus = 'ready';
    retryCount = 0;
    notify();
  }).catch(() => {
    loadStatus = 'error';
    notify();
  }).finally(() => {
    clearTimeout(timer);
    loading = null;
    // Recover transient failures quietly, with a bounded backoff while a price page is mounted.
    if (loadStatus === 'error' && refreshUsers > 0 && retryCount < 3) {
      const delay = [1500, 5000, 15000][retryCount++];
      retryTimer = setTimeout(() => { retryTimer = null; loadPackagePrices({ refresh: true }); }, delay);
    }
  });
  notify();
  return loading;
}

function refreshPrices() {
  retryCount = 0;
  loadPackagePrices({ refresh: true });
}
function visibleRefresh() {
  if (document.visibilityState !== 'hidden') refreshPrices();
}
function changedInAnotherTab(event) {
  if (event.key === PRICE_CHANGE_KEY) refreshPrices();
}
function watchPriceChanges() {
  refreshUsers += 1;
  if (refreshUsers === 1) {
    window.addEventListener('focus', visibleRefresh);
    document.addEventListener('visibilitychange', visibleRefresh);
    window.addEventListener('storage', changedInAnotherTab);
    window.addEventListener(PRICE_CHANGE_EVENT, refreshPrices);
  }
  return () => {
    refreshUsers -= 1;
    if (refreshUsers === 0) {
      clearRetry();
      retryCount = 0;
      window.removeEventListener('focus', visibleRefresh);
      document.removeEventListener('visibilitychange', visibleRefresh);
      window.removeEventListener('storage', changedInAnotherTab);
      window.removeEventListener(PRICE_CHANGE_EVENT, refreshPrices);
    }
  };
}

// The admin editor announces a successful save; other tabs then read the authoritative prices again.
// Only an invalidation timestamp is shared, never prices or checkout amounts.
export function announcePaperworkPricesChanged() {
  loadedAt = 0;
  try { window.localStorage.setItem(PRICE_CHANGE_KEY, String(Date.now())); } catch { /* Focus refresh remains available. */ }
  window.dispatchEvent(new Event(PRICE_CHANGE_EVENT));
}

// Public display state only; unavailable prices never become guessed checkout amounts.
export function usePackagePriceStatus() {
  const [status, setStatus] = useState(loadStatus);
  useEffect(() => {
    const listener = () => setStatus(loadStatus);
    listeners.add(listener);
    listener();
    return () => { listeners.delete(listener); };
  }, []);
  return status;
}
// Pages that show prices load them; the cart wrapper around every page only listens ({ load: false }), so pages
// without prices make no request.
export function usePackagePrices({ load = true } = {}) {
  const [prices, setPrices] = useState(current);
  useEffect(() => {
    const listener = () => setPrices(current);
    listeners.add(listener);
    listener();
    const stop = load ? watchPriceChanges() : null;
    if (load) loadPackagePrices({ refresh: true });
    return () => { listeners.delete(listener); stop?.(); };
  }, [load]);
  return prices;
}
// The same prices with the sale details, for the home page cards (original crossed out while a sale is on).
export function usePackagePriceDetails() {
  const [prices, setPrices] = useState(details);
  useEffect(() => {
    const listener = () => setPrices(details);
    listeners.add(listener);
    listener();
    const stop = watchPriceChanges();
    loadPackagePrices({ refresh: true });
    return () => { listeners.delete(listener); stop(); };
  }, []);
  return prices;
}
